import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "./button";
import { useDispatch } from "react-redux";
import { setStep } from "../../redux/Slices/stepSlice";

const OrderUpdateModal = ({ open, onClose, changes, onSubmit }) => {
  const dispatch = useDispatch();

  if (!changes) return null;

  const {
    restaurantInfoUpdated = [],
    itemsRemoved = [],
    itemsUpdated = [],
    totalsUpdated = null,
    cartEmptied = false
  } = changes;

  const handleEmptyCart = () => {
    dispatch(setStep("menu"));
    onClose();
  }

  const hasAnyChanges =
    restaurantInfoUpdated.length > 0 ||
    itemsRemoved.length > 0 ||
    itemsUpdated.length > 0 ||
    Boolean(totalsUpdated);

  if (!hasAnyChanges) return null;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Background */}
          <motion.div
            className="fixed inset-0 bg-black/50 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Popup */}
          <motion.div
            className="fixed inset-0 flex justify-center items-center z-50 p-4"
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.85, opacity: 0 }}
          >
            {/* KIOSK CARD */}
            <div
              className="
                w-full 
                max-w-[900px]
                max-h-[85vh]
                bg-white 
                rounded-2xl 
                shadow-2xl 
                p-6 
                flex 
                flex-col 
                relative
              "
            >

              {/* HEADER */}
              <section>
                <h2 className="font-semibold text-2xl">YOUR ORDER WAS UPDATED</h2>
                <p className="text-gray-600 text-lg">
                  Some menu or restaurant changes occurred. Review before checkout.
                </p>
              </section>

              {/* SCROLLABLE */}
              <div className="flex-1 overflow-y-auto mt-6 space-y-6 pr-2">

                {/* RESTAURANT INFO UPDATED */}
                {restaurantInfoUpdated.length > 0 && (
                  <section>
                    <h3 className="font-semibold text-xl mb-3">RESTAURANT INFO UPDATED</h3>
                    {restaurantInfoUpdated.map((info, idx) => (
                      <div
                        key={idx}
                        className="border rounded-lg p-4 bg-gray-50 text-lg"
                      >
                        <div className="font-medium capitalize">
                          {info.field.replace("_", " ")}
                        </div>
                        <div className="mt-1">
                          <span className="text-red-500 line-through mr-2">
                            {String(info.oldValue || "-")}
                          </span>
                          <span className="mx-2">→</span>
                          <span className="text-blue-600 font-bold">
                            {String(info.newValue || "-")}
                          </span>
                        </div>
                      </div>
                    ))}
                  </section>
                )}

                {/* ITEMS REMOVED */}
                {itemsRemoved.length > 0 && (
                  <section>
                    <h3 className="font-semibold text-xl mb-3">ITEMS REMOVED</h3>
                    {itemsRemoved.map((item, idx) => (
                      <div
                        key={idx}
                        className="border rounded-lg p-4 bg-gray-50 text-lg"
                      >
                        <span className="font-medium">{item.name}</span> — {item.reason}
                      </div>
                    ))}
                  </section>
                )}

                {/* ITEMS UPDATED */}
                {itemsUpdated.length > 0 && (
                  <section>
                    <h3 className="font-semibold text-xl mb-3">ITEMS UPDATED</h3>
                    {itemsUpdated.map((item, idx) => (
                      <div
                        key={idx}
                        className="border rounded-lg p-4 bg-gray-50 text-lg"
                      >
                        <div className="font-semibold">{item.name}</div>

                        {item.priceChanged && (
                          <div className="mt-1">
                            Price:
                            <span className="line-through text-red-500 ml-2">
                              ${item.oldPrice}
                            </span>
                            <span className="mx-2">→</span>
                            <span className="text-blue-600 font-bold">
                              ${item.newPrice}
                            </span>
                          </div>
                        )}

                        {item.removedModifiers.length > 0 && (
                          console.log("REMOVED MODIFIERS", JSON.stringify(item.removedModifiers)),
                          <ul className="list-disc ml-6 mt-2">
                            {item.removedModifiers.map((mod, i) => (
                              <li key={i}>Modifier: {mod.name} removed</li>
                            ))}
                          </ul>
                        )}
                        {/* MODIFIER NAME CHANGES */}
                        {item.modifierNameChanges.length > 0 && (
                          <ul className="list-disc ml-6 mt-2">
                            {item.modifierNameChanges.map((mod, i) => (
                              <li key={i}>
                                {mod.name}:{" "}
                                <span className="line-through text-red-500">
                                  {mod.oldName}
                                </span>{" "}
                                →{" "}
                                <span className="text-blue-600 font-bold">
                                  {mod.newName}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {/* MODIFIER PRICE CHANGES */}
                        {item.modifierPriceChanges.length > 0 && (
                          <ul className="list-disc ml-6 mt-2">
                            {item.modifierPriceChanges.map((mod, i) => (
                              <li key={i}>
                                {mod.name}:{" "}
                                <span className="line-through text-red-500">
                                  ${mod.oldPrice}
                                </span>{" "}
                                →{" "}
                                <span className="text-blue-600 font-bold">
                                  ${mod.newPrice}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {/* MODIFIER QTY CHANGES */}
                        {item.modifierQtyChanges.length > 0 && (
                          <ul className="list-disc ml-6 mt-2">
                            {item.modifierQtyChanges.map((mod, i) => (
                              <li key={i}>
                                {mod.name}:{" "}
                                <span className="line-through text-red-500">
                                  {mod.oldQty}
                                </span>{" "}
                                →{" "}
                                <span className="text-blue-600 font-bold">
                                  {mod.newQty}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </section>
                )}

                {/* TOTALS UPDATED */}
                {totalsUpdated && (
                  <section>
                    <h3 className="font-semibold text-xl mb-3">TOTALS UPDATED</h3>
                    <div className="text-lg space-y-2">
                      <div>
                        Subtotal:
                        <span className="line-through text-red-500 ml-2">
                          ${totalsUpdated.oldSubtotal}
                        </span>
                        {" → "}
                        <span className="text-blue-600 font-semibold">
                          ${totalsUpdated.newSubtotal}
                        </span>
                      </div>

                      <div>
                        Tax:
                        <span className="line-through text-red-500 ml-2">
                          ${totalsUpdated.oldTax}
                        </span>
                        {" → "}
                        <span className="text-blue-600 font-semibold">
                          ${totalsUpdated.newTax}
                        </span>
                      </div>

                      <div>
                        Total:
                        <span className="line-through text-red-500 ml-2">
                          ${totalsUpdated.oldTotal}
                        </span>
                        {" → "}
                        <span className="text-blue-600 font-semibold">
                          ${totalsUpdated.newTotal}
                        </span>
                      </div>
                    </div>
                  </section>
                )}
              </div>

              {/* FOOTER BUTTONS */}
              <div className="pt-6 border-t">
                {cartEmptied ? (
                  <Button
                    variant="orange"
                    className="w-full py-4 text-xl"
                    onClick={handleEmptyCart}   // or navigate to menu page
                  >
                    GO BACK TO MENU
                  </Button>
                ) : (
                  <div className="flex justify-between">
                    <Button
                      variant="ghost"
                      className="w-[48%] py-4 text-xl border"
                      onClick={onClose}
                    >
                      BACK TO CART
                    </Button>

                    <Button
                      variant="orange"
                      className="w-[48%] py-4 text-xl"
                      onClick={onSubmit}
                    >
                      CONTINUE TO CHECKOUT
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default OrderUpdateModal;
